import { getOrderInfo } from '@/components/table/SortableTableHead';

import { authedFetch } from '@/components/Apollo/ApolloWrapper';

import { getRestApiUrl } from '../getRestApiUrl';

import { naturalSort } from './naturalSort';

export type File = Readonly<{
  filename: string | null | undefined;
  folder: boolean | null | undefined;
  pinned?: boolean | null | undefined;
  lastUploaded: null | undefined | Date | string;
  lastModified: null | Date | string | undefined;
}>;

function filenameSorter(fileA: File, fileB: File) {
  // Maybe strip off any file extensions
  const filenameA = fileA.folder
    ? fileA.filename
    : fileA.filename?.replace(/\.\w+$/, '');
  const filenameB = fileB.folder
    ? fileB.filename
    : fileB.filename?.replace(/\.\w+$/, '');

  return naturalSort(filenameA, filenameB);
}

export function sortFiles<F extends File>(
  input: ReadonlyArray<F>,
  orderBy: string | undefined,
  separatePinned = false,
): F[] {
  const { id, direction } = getOrderInfo(orderBy);

  const isPinnedForSeparation = (file: F) => {
    return Boolean(separatePinned && file.pinned);
  };

  const pinnedFiles = input.filter(
    (file) => file.filename && isPinnedForSeparation(file) && !file.folder,
  );
  const pinnedFolders = input.filter(
    (file) => file.filename && isPinnedForSeparation(file) && file.folder,
  );
  const unpinnedFiles = input.filter(
    (file) => file.filename && !isPinnedForSeparation(file) && !file.folder,
  );
  const unpinnedFolders = input.filter(
    (file) => file.filename && !isPinnedForSeparation(file) && file.folder,
  );

  let sorter;
  switch (id) {
    case 'lastModified':
      sorter = lastModifiedSorter;
      break;
    default:
      sorter = filenameSorter;
      break;
  }

  pinnedFiles.sort(sorter);
  pinnedFolders.sort(sorter);
  unpinnedFiles.sort(sorter);
  unpinnedFolders.sort(sorter);

  if (direction === 'desc') {
    unpinnedFiles.reverse();
    unpinnedFolders.reverse();
    pinnedFiles.reverse();
    pinnedFolders.reverse();
  }

  return [
    ...pinnedFolders,
    ...pinnedFiles,
    ...unpinnedFolders,
    ...unpinnedFiles,
  ];
}

function lastModifiedSorter(a: File, b: File) {
  const aField = new Date(a.lastUploaded || a.lastModified || -Infinity);
  const bField = new Date(b.lastUploaded || b.lastModified || -Infinity);

  const res = aField.getTime() - bField.getTime();

  // Tie - go by name
  if (res === 0) {
    return filenameSorter(a, b);
  }

  return res;
}

export async function downloadAllFilesForOwner(
  ownerId: string,
  ownerName: string,
) {
  const qry = {
    hidden: { $ne: true },
    owner: ownerId,
    $or: [{ parent: { $exists: false } }, { parent: null }],
  };

  const url = new URL(`${getRestApiUrl()}/files/downloadUrl`);
  url.searchParams.set('_q', JSON.stringify(qry));
  url.searchParams.set('name', ownerName);

  const res = await authedFetch(url.href);
  const { url: url_1 } = await res.json();
  window.location = url_1;
}

export async function downloadFile(fileId: string) {
  const res = await authedFetch(
    `${getRestApiUrl()}/files/downloadUrl/${fileId}`,
  );
  const { url } = await res.json();
  window.location = url;
}

// Currently unused
export async function downloadFileUrl(fileId: string) {
  const res = await authedFetch(
    `${getRestApiUrl()}/files/downloadUrl/${fileId}?inline=true`,
  );
  const { url } = await res.json();
  return url;
}

// Currently unused
export async function searchFiles(ownerIds: string[], searchText: string) {
  const searchParams = new URLSearchParams();

  ownerIds.forEach((ownerId) => {
    searchParams.append('owner', ownerId);
  });

  searchParams.append('q', searchText);

  const res = await authedFetch(
    `${getRestApiUrl()}/search/files?${searchParams.toString()}`,
  );

  return res.json();
}

const FILE_EXTENSION_RE = /\.[\w]+$/;

export function getFileDisplayName({
  filename,
  folder,
}: Readonly<{
  filename: string | null | undefined;
  folder: boolean | null | undefined;
}>) {
  const filenameStr = filename ?? '';

  const fileExtensionMatch = folder
    ? null
    : filenameStr.match(FILE_EXTENSION_RE);
  return fileExtensionMatch
    ? filenameStr.replace(FILE_EXTENSION_RE, '')
    : filename;
}

export function getFileExtension({
  filename,
  folder,
}: Readonly<{
  filename: string | null | undefined;
  folder: boolean | null | undefined;
}>) {
  const filenameStr = filename ?? '';
  const fileExtensionMatch = folder
    ? null
    : filenameStr.match(FILE_EXTENSION_RE);

  return fileExtensionMatch && fileExtensionMatch[0];
}
